import { VisuallyHidden } from '@radix-ui/react-visually-hidden'
import classNames from 'classnames'
import { type DOMAttributes, forwardRef } from 'react'
import { Icon2 } from '../Icon2'
import { selectors } from './styles'
import {
  type ButtonBaseProps,
  type ButtonIconProps,
  type ButtonLabelProps,
  type ButtonNativeProps,
  type FluentProps,
} from './types'

export {
  cssVariables as buttonLikeCSSVariables,
  selectors as buttonLikeSelectors,
} from './styles'
export {
  type AccessibleName,
  type ButtonBaseProps,
  type ButtonIconProps,
  type ButtonNativeProps,
  type DeclarativeContent,
  type FluentContent,
  type FluentProps,
  type StyleProps,
} from './types'

///////////////////////////////////////////////////////////////////////////////////////
// `ButtonLike` is a collection of reusable pieces for building button-like components.
//
// You get consistent behavior and styling without doing a bunch of overrides on another component. This makes the
// system simpler to use and easier to maintain.
///////////////////////////////////////////////////////////////////////////////////////

/**
 * Implementation detail: the native `disabled` attribute blocks all interaction with the `<button>`, so users can’t
 * reach it to find out _why_ it’s disabled. `aria-disabled` keeps the button reachable, but it doesn’t block events the
 * way `disabled` would, so the relevant handlers need to be short-circuited.
 *
 * Don’t use this within components that manage their own focus state.
 */
export const DISABLED_HANDLER_OVERRIDES: Partial<
  Record<keyof DOMAttributes<HTMLElement>, undefined>
> = {
  onClick: undefined,
  onDoubleClick: undefined,
  // Mouse click not from left button
  onAuxClick: undefined,

  onMouseDown: undefined,
  onMouseUp: undefined,
  onPointerDown: undefined,
  onPointerUp: undefined,
  onTouchStart: undefined,
  onTouchEnd: undefined,

  onKeyDown: undefined,
  onKeyUp: undefined,
} as const

export function resolveDisabledState(
  disabled: ButtonNativeProps['disabled'],
  ariaDisabled: ButtonNativeProps['aria-disabled']
): boolean {
  const resolvedValue = disabled ?? ariaDisabled ?? false
  return resolvedValue !== false && resolvedValue !== 'false'
}

/**
 * Unstyled `<button>` that accepts `size` and `variant` props, and implements `disabled` in an accessible way.
 *
 * @warning Components outside `@opallabs/lapidary` MUST NOT use this.
 */
export const ButtonBase = forwardRef<HTMLButtonElement, ButtonBaseProps>(
  function ButtonBase(props, ref): JSX.Element {
    const {
      'aria-disabled': ariaDisabled,
      disabled,
      size,
      variant,
      ...remainingProps
    } = props

    // The native `disabled` attribute blocks all interaction with the `<button>`, so users can't reach it to find out
    // _why_ it's disabled. `aria-disabled` has the desired semantics.
    const isDisabled = resolveDisabledState(disabled, ariaDisabled)

    return (
      <button
        // `remainingProps` may have its own definition of these specific props. (For example, `<Button2>`'s
        // `resolveDeclarativeProps`.)
        data-size={size}
        data-variant={variant}
        ref={ref}
        type="button"
        {...remainingProps}
        aria-disabled={isDisabled}
        {...(isDisabled ? DISABLED_HANDLER_OVERRIDES : undefined)}
      />
    )
  }
)
ButtonBase.displayName = 'Lapidary: ButtonBase ← ButtonLike'

/**
 * @note Avoid modifying styles unless it’s absolutely necessary.
 * @warning Components outside `@opallabs/lapidary` MUST NOT use this.
 */
export const ButtonIcon = ({
  className,
  glyph,
  size,
}: ButtonIconProps): JSX.Element => (
  <Icon2
    aria-hidden
    className={classNames(selectors.icon, className)}
    glyph={glyph}
    size={size}
  />
)
ButtonIcon.displayName = 'Lapidary: ButtonIcon ← ButtonLike'

/**
 * @note Avoid modifying styles unless it’s absolutely necessary.
 * @warning Components outside `@opallabs/lapidary` MUST NOT use this.
 */
export const ButtonLabel = ({
  children,
  className,
  visuallyHidden = false,
}: ButtonLabelProps): JSX.Element => {
  const node = (
    <span className={classNames(selectors.label, className)}>{children}</span>
  )
  if (!visuallyHidden) return node

  return <VisuallyHidden>{node}</VisuallyHidden>
}
ButtonLabel.displayName = 'Lapidary: ButtonLabel ← ButtonLike'

/**
 * Convert the fluent API (string `children`, `glyph`) into `<ButtonIcon>`/`<ButtonLabel>`. Ensures the button has an
 * accessible name.
 */
export function resolveFluentContent(
  props: FluentProps,
  componentName: string
): JSX.Element {
  const { children, glyph } = props
  const hasChildText = children !== undefined

  if (
    glyph &&
    !hasChildText &&
    !props['aria-label'] &&
    !props['aria-labelledby']
  ) {
    throw new TypeError(
      `${componentName}: glyph must be accompanied by child text, aria-label, or aria-labelledby`
    )
  }

  return (
    <>
      {glyph && <ButtonIcon glyph={glyph} />}
      {hasChildText && <ButtonLabel>{children}</ButtonLabel>}
    </>
  )
}
